import React, { useState, useEffect } from "react";
import { Card, Steps, Button, Row, Col, Space, Typography, Spin, notification } from "antd";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import TestRunDescription from "./Steps/TestRunDescription";
import TestRunConfig from "./Steps/TestRunConfig";
import TestRunReview from "./Steps/TestRunReview";
import { TestRunService } from "../../services/TestRunService";
import { usePermissions } from "../../PermissionsProvider";
import { Config } from "../../model_gen/config";
import { IntentGroup, TestRun, TestSet } from "../../model_gen/evaluation";
import { IntentService } from "../../services/IntentService";
import { TestSetService } from "../../services/TestSetService";

const { Step } = Steps;
const { Text } = Typography;

interface TestRunData {
  description?: string;
  config?: Config;
  test_set_id?: number;
}

export const NEW_TEST_RUN_STORAGE_KEY = "testRunData";

const TestRunNew: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { intentGroupId, testSetId } = useParams();
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const step = searchParams.get("step");
    return step ? parseInt(step) : 0;
  });
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const [testSet, setTestSet] = useState<TestSet | null>(null);
  const [testRunData, setTestRunData] = useState<TestRunData>(() => {
    const saved = localStorage.getItem(NEW_TEST_RUN_STORAGE_KEY);
    return saved ? JSON.parse(saved) : { test_set_id: Number(testSetId) };
  });

  useEffect(() => {
    localStorage.setItem(NEW_TEST_RUN_STORAGE_KEY, JSON.stringify(testRunData));
  }, [testRunData]);

  useEffect(() => {
    setSearchParams({ step: currentStep.toString() });
  }, [currentStep, setSearchParams]);

  useEffect(() => {
    if (intentGroupId && testSetId) {
      IntentService.getIntentGroup(Number(intentGroupId), false, setLoading, apiKey).subscribe(
        (intentGroup: IntentGroup) => {
          setIntentGroup(intentGroup);
          setTestRunData(prev => ({ ...prev, config: intentGroup.config }));
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [intentGroupId, apiKey]);

  useEffect(() => {
    if (testSetId) {
      TestSetService.getTestSet(String(testSetId), setLoading, apiKey)
        .subscribe((testSet: TestSet) => {
          setTestSet(testSet);
        });
    }
  }, [testSetId, apiKey]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const steps = [
    {
      title: "Description",
      description: "Set description",
      component: (
        <TestRunDescription
          description={testRunData.description}
          onChange={(description: string) => 
            setTestRunData(prev => ({ ...prev, description }))
          }
        />
      ),
    },
    {
      title: "Configuration",
      description: "Configure settings",
      component: (
        <TestRunConfig
          intentGroupId={Number(intentGroupId)}
          testSetId={Number(testSetId)}
          config={testRunData.config}
          onChange={(config: Config) => setTestRunData(prev => ({ ...prev, config }))}
        />
      ),
    },
    {
      title: "Review",
      description: "Confirm settings",
      component: (
        <TestRunReview
          testRunData={testRunData}
          testSetId={Number(testSetId)}
        />
      ),
    },
  ];

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return testRunData.description && testRunData.description.trim().length > 0;
      case 1:
        return testRunData.config !== undefined;
      case 2:
        return true;
      default:
        return false;
    }
  };

  const createTestRun = async () => {
    const payload = {
      description: testRunData.description,
      test_set_id: Number(testSetId),
      intent_group_id: Number(intentGroupId),
      config: testRunData.config,
    };

    TestRunService.createTestRun(payload, setLoading, apiKey).subscribe((response: TestRun) => {
      console.log(`Test run created successfully: ${response.id}`);
      localStorage.removeItem(NEW_TEST_RUN_STORAGE_KEY);
      notification.success({
        message: "Test run created successfully",
        description: "Your test run has been created successfully",
      });
      navigate(`/intent-group/${intentGroupId}/testsets/${testSetId}`);
    }, (error: any) => {
      notification.error({
        message: "Error creating test run",
        description: error.message,
      });
    });
  };

  return (
    <Card className="test-runs-wrapper">
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Typography style={{ fontSize: "24px", fontWeight: "600" }}>
              {(!intentGroup || !testSet) ? (
                <Spin size="small" />
              ) : (
                <>
                  <Link
                    to={"/intent-groups"}
                    style={{ color: "#828282" }}>
                    Intent Groups
                    {" / "}{" "}
                  </Link>
                  <Link
                    to={`/intent-group/${intentGroup.id}`}
                    style={{ color: "#828282" }}>
                    {intentGroup.intent_name}
                    {" / "}{" "}
                  </Link>
                  <Link
                    to={`/intent-group/${intentGroup.id}/testsets/${testSetId}`}
                    style={{ color: "#828282" }}>
                    Test Sets
                    {" / "}{" "}
                  </Link>
                  New Test Run
                </>
              )}
            </Typography>
            <Text type="secondary" style={{ display: "block", marginBottom: "24px" }}>
              Step {currentStep + 1} of {steps.length}
            </Text>
          </Col>
        </Row>

        <Steps 
          current={currentStep} 
          progressDot 
          style={{ margin: "12px 0" }}
        >
          {steps.map(step => (
            <Step 
              key={step.title} 
              title={step.title} 
              description={step.description}
            />
          ))}
        </Steps>

        <Card 
          bordered={false} 
          className="step-content"
          style={{ 
            padding: "24px",
            borderRadius: "8px",
            margin: "12px 0"
          }}
        >
          {steps[currentStep].component}
        </Card>

        <Row justify="space-between">
          <Col>
            {currentStep > 0 && (
              <Button 
                size="large"
                onClick={handlePrev}
                disabled={loading}
              >
                Previous Step
              </Button>
            )}
          </Col>
          <Col>
            {currentStep < steps.length - 1 ? (
              <Button 
                type="primary"
                size="large"
                onClick={handleNext}
                disabled={!canProceed() || loading}
              >
                Continue
              </Button>
            ) : (
              <Button 
                type="primary"
                size="large"
                disabled={!canProceed() || loading}
                onClick={createTestRun}
                icon={loading ? <Spin size="small" /> : null}
              >
                {loading ? "Creating test run..." : "Create Test Run"}
              </Button>
            )}
          </Col>
        </Row>
      </Space>
    </Card>
  );
};

export default TestRunNew;
